import React, { useCallback, useRef, useState, useEffect } from "react";

/**
 * ProfilePhotoEditor
 * -------------------
 * Lets a person pick a photo from their device, pan/zoom it inside a
 * fixed frame, then bakes the result down to an exact 320x320 PNG
 * before handing it off to `onUpload`.
 *
 * Drop this into any React + Tailwind + TypeScript project. No crop
 * library required — panning/zooming and the final resize are done
 * with a plain <canvas>.
 */

const OUTPUT_SIZE = 320; // px, both dimensions
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_FILE_BYTES = 8 * 1024 * 1024; // 8MB guard, adjust as needed

type Stage = "empty" | "editing" | "uploading" | "done" | "error";

interface ProfilePhotoEditorProps {
	/** Called with the final 320x320 image once the person confirms. */
	onUpload?: (file: Blob) => Promise<void> | void;
	/** Existing avatar to show before the person picks a new one. */
	currentPhotoUrl?: string;
	/** Display name, used only for the placeholder initial. */
	displayName?: string;
}

interface Offset {
	x: number;
	y: number;
}

export function ProfilePhotoEditor({
	onUpload,
	currentPhotoUrl,
	displayName = "",
}: ProfilePhotoEditorProps) {
	const [stage, setStage] = useState<Stage>("empty");
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [imageEl, setImageEl] = useState<HTMLImageElement | null>(null);
	const [zoom, setZoom] = useState<number>(MIN_ZOOM);
	const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
	const [resultUrl, setResultUrl] = useState<string>(currentPhotoUrl ?? "");
	const [isDragging, setIsDragging] = useState(false);

	const fileInputRef = useRef<HTMLInputElement>(null);
	const frameRef = useRef<HTMLDivElement>(null);
	const dragStart = useRef<{ pointerX: number; pointerY: number; offset: Offset } | null>(null);

	const FRAME_DISPLAY_SIZE = 240; // on-screen preview size, output is still baked at 320x320

	// Clean up object URLs we create so we don't leak memory.
	useEffect(() => {
		return () => {
			if (imageEl?.src.startsWith("blob:")) URL.revokeObjectURL(imageEl.src);
		};
	}, [imageEl]);

	const resetToEmpty = useCallback(() => {
		setStage(currentPhotoUrl ? "empty" : "empty");
		setImageEl(null);
		setZoom(MIN_ZOOM);
		setOffset({ x: 0, y: 0 });
		setErrorMessage("");
		if (fileInputRef.current) fileInputRef.current.value = "";
	}, [currentPhotoUrl]);

	const loadFile = useCallback((file: File) => {
		setErrorMessage("");

		if (!ACCEPTED_TYPES.includes(file.type)) {
			setStage("error");
			setErrorMessage("Please choose a PNG, JPEG, or WEBP image.");
			return;
		}
		if (file.size > MAX_FILE_BYTES) {
			setStage("error");
			setErrorMessage("That image is larger than 8MB. Choose a smaller file.");
			return;
		}

		const url = URL.createObjectURL(file);
		const img = new Image();
		img.onload = () => {
			setImageEl(img);
			setZoom(MIN_ZOOM);
			setOffset({ x: 0, y: 0 });
			setStage("editing");
		};
		img.onerror = () => {
			setStage("error");
			setErrorMessage("That file couldn't be read as an image.");
			URL.revokeObjectURL(url);
		};
		img.src = url;
	}, []);

	const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) loadFile(file);
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		const file = e.dataTransfer.files?.[0];
		if (file) loadFile(file);
	};

	// ---- Panning ----
	const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
		if (!imageEl) return;
		(e.target as Element).setPointerCapture(e.pointerId);
		setIsDragging(true);
		dragStart.current = { pointerX: e.clientX, pointerY: e.clientY, offset };
	};

	const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
		if (!isDragging || !dragStart.current || !imageEl) return;
		const dx = e.clientX - dragStart.current.pointerX;
		const dy = e.clientY - dragStart.current.pointerY;
		const next = clampOffset(
			{ x: dragStart.current.offset.x + dx, y: dragStart.current.offset.y + dy },
			imageEl,
			zoom
		);
		setOffset(next);
	};

	const handlePointerUp = () => {
		setIsDragging(false);
		dragStart.current = null;
	};

	// Keep the image covering the frame at all times, however it's panned.
	function clampOffset(candidate: Offset, img: HTMLImageElement, currentZoom: number): Offset {
		const scale = getCoverScale(img) * currentZoom;
		const scaledW = img.width * scale;
		const scaledH = img.height * scale;
		const maxX = Math.max(0, (scaledW - FRAME_DISPLAY_SIZE) / 2);
		const maxY = Math.max(0, (scaledH - FRAME_DISPLAY_SIZE) / 2);
		return {
			x: Math.min(maxX, Math.max(-maxX, candidate.x)),
			y: Math.min(maxY, Math.max(-maxY, candidate.y)),
		};
	}

	function getCoverScale(img: HTMLImageElement): number {
		return Math.max(FRAME_DISPLAY_SIZE / img.width, FRAME_DISPLAY_SIZE / img.height);
	}

	const handleZoomChange = (value: number) => {
		if (!imageEl) return;
		setZoom(value);
		setOffset((prev) => clampOffset(prev, imageEl, value));
	};

	// ---- Bake the final 320x320 image on a canvas ----
	const buildOutputBlob = useCallback((): Promise<Blob | null> => {
		return new Promise((resolve) => {
			if (!imageEl) return resolve(null);

			const canvas = document.createElement("canvas");
			canvas.width = OUTPUT_SIZE;
			canvas.height = OUTPUT_SIZE;
			const ctx = canvas.getContext("2d");
			if (!ctx) return resolve(null);

			const displayScale = getCoverScale(imageEl) * zoom;
			// Convert from on-screen preview units to output units.
			const outputScale = displayScale * (OUTPUT_SIZE / FRAME_DISPLAY_SIZE);
			const drawW = imageEl.width * outputScale;
			const drawH = imageEl.height * outputScale;
			const outputOffsetX = offset.x * (OUTPUT_SIZE / FRAME_DISPLAY_SIZE);
			const outputOffsetY = offset.y * (OUTPUT_SIZE / FRAME_DISPLAY_SIZE);

			const dx = (OUTPUT_SIZE - drawW) / 2 + outputOffsetX;
			const dy = (OUTPUT_SIZE - drawH) / 2 + outputOffsetY;

			ctx.imageSmoothingEnabled = true;
			ctx.imageSmoothingQuality = "high";
			ctx.drawImage(imageEl, dx, dy, drawW, drawH);

			canvas.toBlob((blob) => resolve(blob), "image/png", 0.92);
		});
	}, [imageEl, zoom, offset]);

	const handleConfirm = async () => {
		const blob = await buildOutputBlob();
		if (!blob) {
			setStage("error");
			setErrorMessage("Something went wrong preparing that image. Try again.");
			return;
		}

		setStage("uploading");
		try {
			await onUpload?.(blob);
			const previewUrl = URL.createObjectURL(blob);
			setResultUrl((old) => {
				if (old?.startsWith("blob:")) URL.revokeObjectURL(old);
				return previewUrl;
			});
			setStage("done");
		} catch (err) {
			setStage("error");
			setErrorMessage(err instanceof Error ? err.message : "Upload failed. Try again.");
		}
	};

	const initial = displayName.trim().charAt(0).toUpperCase() || "?";

	return (
		<div className="w-full max-w-sm rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
			<div className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-teal-700">
				Account
			</div>
			<h2 className="mb-6 text-lg font-semibold text-stone-900">Profile photo</h2>

			{/* ---------- EMPTY / current photo state ---------- */}
			{stage === "empty" && (
				<div>
					<div className="mb-5 flex items-center gap-4">
						<div className="h-20 w-20 shrink-0 overflow-hidden rounded-full bg-stone-100 ring-1 ring-stone-200">
							{resultUrl ? (
								<img src={resultUrl} alt="Current profile" className="h-full w-full object-cover" />
							) : (
								<div className="flex h-full w-full items-center justify-center text-2xl font-medium text-stone-400">
									{initial}
								</div>
							)}
						</div>
						<div className="text-sm text-stone-500">
							PNG, JPEG, or WEBP. Any size — it will be resized to {OUTPUT_SIZE}×{OUTPUT_SIZE}.
						</div>
					</div>

					<div
						onDragOver={(e) => e.preventDefault()}
						onDrop={handleDrop}
						className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-stone-300 px-4 py-8 text-center transition-colors hover:border-teal-500 hover:bg-teal-50/40"
					>
						<p className="mb-3 text-sm text-stone-500">Drag a photo here, or</p>
						<button
							type="button"
							onClick={() => fileInputRef.current?.click()}
							className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-800 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-teal-700"
						>
							Choose a photo
						</button>
					</div>
					<input
						ref={fileInputRef}
						type="file"
						accept={ACCEPTED_TYPES.join(",")}
						onChange={handleFileInputChange}
						className="hidden"
					/>
				</div>
			)}

			{/* ---------- ERROR state ---------- */}
			{stage === "error" && (
				<div>
					<div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
						{errorMessage}
					</div>
					<button
						type="button"
						onClick={resetToEmpty}
						className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
					>
						Try again
					</button>
					<input
						ref={fileInputRef}
						type="file"
						accept={ACCEPTED_TYPES.join(",")}
						onChange={handleFileInputChange}
						className="hidden"
					/>
				</div>
			)}

			{/* ---------- EDITING state: pan + zoom ---------- */}
			{stage === "editing" && imageEl && (
				<div>
					<div
						ref={frameRef}
						onPointerDown={handlePointerDown}
						onPointerMove={handlePointerMove}
						onPointerUp={handlePointerUp}
						onPointerLeave={handlePointerUp}
						className="relative mx-auto mb-5 overflow-hidden rounded-full bg-stone-100 ring-1 ring-stone-200"
						style={{
							width: FRAME_DISPLAY_SIZE,
							height: FRAME_DISPLAY_SIZE,
							cursor: isDragging ? "grabbing" : "grab",
							touchAction: "none",
						}}
					>
						<img
							src={imageEl.src}
							alt="Photo being edited"
							draggable={false}
							className="pointer-events-none absolute left-1/2 top-1/2 max-w-none select-none"
							style={{
								width: imageEl.width * getCoverScale(imageEl) * zoom,
								height: imageEl.height * getCoverScale(imageEl) * zoom,
								transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
							}}
						/>
					</div>

					<label className="mb-1 block text-xs font-medium text-stone-500">Zoom</label>
					<input
						type="range"
						min={MIN_ZOOM}
						max={MAX_ZOOM}
						step={0.01}
						value={zoom}
						onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
						className="mb-6 w-full accent-teal-700"
					/>

					<div className="flex gap-3">
						<button
							type="button"
							onClick={resetToEmpty}
							className="flex-1 rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={handleConfirm}
							className="flex-1 rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800"
						>
							Save photo
						</button>
					</div>
				</div>
			)}

			{/* ---------- UPLOADING state ---------- */}
			{stage === "uploading" && (
				<div className="flex flex-col items-center justify-center py-10">
					<div className="mb-4 h-10 w-10 animate-spin rounded-full border-2 border-stone-200 border-t-teal-700" />
					<p className="text-sm text-stone-500">Uploading your photo…</p>
				</div>
			)}

			{/* ---------- DONE state ---------- */}
			{stage === "done" && (
				<div className="flex flex-col items-center">
					<div className="mb-4 h-24 w-24 overflow-hidden rounded-full ring-2 ring-teal-600 ring-offset-2">
						<img src={resultUrl} alt="Updated profile" className="h-full w-full object-cover" />
					</div>
					<p className="mb-5 text-sm font-medium text-stone-700">Profile photo updated</p>
					<button
						type="button"
						onClick={() => setStage("empty")}
						className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
					>
						Change again
					</button>
				</div>
			)}
		</div>
	);
}